import React, { useEffect, useState } from 'react';
import { apiFetch } from '../lib/apiFetch';
import { AlertasReporte, ApiResponse } from '../types';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

const ICON_BY_TIPO: Record<string, string> = {
    bloqueo_largo: '🚫',
    wip_max: '⚠️',
    backlog_90d: '📋',
    backlog_30_90d: '⏰',
};

const COLOR_BY_TIPO: Record<string, string> = {
    bloqueo_largo: 'bg-red-50 border-red-300',
    wip_max: 'bg-amber-50 border-amber-300',
    backlog_90d: 'bg-red-50 border-red-200',
    backlog_30_90d: 'bg-amber-50 border-amber-200',
};

export const AlertasPanel: React.FC = () => {
    const [data, setData] = useState<AlertasReporte | null>(null);
    const [loading, setLoading] = useState(false);

    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await apiFetch(`${API_BASE}/tareas/alertas`);
            const json: ApiResponse<AlertasReporte> = await res.json();
            if (json.success && json.data) setData(json.data);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, []);

    if (!data) {
        return <div className="text-center text-slate-400 py-8">{loading ? 'Cargando...' : 'Sin datos'}</div>;
    }

    const totalAlertas =
        data.bloqueados_mas_3d +
        data.wip_al_maximo.length +
        data.backlog_mas_90d;

    return (
        <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h3 className="text-base font-semibold text-slate-800">🚨 Alertas del Sistema</h3>
                    <p className="text-xs text-slate-500">
                        Lo que requiere atención inmediata. {totalAlertas === 0 && <span className="text-emerald-600 font-medium">✅ Todo en orden.</span>}
                    </p>
                </div>
                <button
                    onClick={fetchData}
                    disabled={loading}
                    className="px-3 py-1 text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 rounded"
                >
                    {loading ? '⟳' : '↻'} Refrescar
                </button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
                <StatBox label="Bloqueados" value={data.bloqueados_total} sub={`${data.bloqueados_mas_3d} > 3d`} color="red" />
                <StatBox label="WIP al máx" value={data.wip_al_maximo.length} sub="columnas saturadas" color="amber" />
                <StatBox label="Backlog 90d+" value={data.backlog_mas_90d} sub="candidatos a archivar" color="red" />
                <StatBox label="Backlog 30-90d" value={data.backlog_30a90d} sub="revisar relevancia" color="amber" />
                <StatBox label="Total alertas" value={totalAlertas} sub="" color={totalAlertas > 0 ? 'red' : 'emerald'} />
            </div>

            {data.wip_al_maximo.length > 0 && (
                <div className="mb-4">
                    <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">
                        ⚠️ WIP al máximo
                    </h4>
                    <div className="space-y-1">
                        {data.wip_al_maximo.map(w => (
                            <div key={w.estado} className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded px-3 py-2 text-sm">
                                <span className="font-medium text-slate-800">{w.estado}</span>
                                <span className="font-mono text-amber-800">{w.actual}/{w.limite}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">Items críticos</h4>
            <div className="space-y-1 max-h-[400px] overflow-y-auto scrollbar-thin">
                {data.items.length === 0 ? (
                    <div className="text-sm text-emerald-600 italic py-4 text-center">✅ Sin items críticos</div>
                ) : (
                    data.items.map((item, idx) => (
                        <div
                            key={`${item.tipo}-${item.id}-${idx}`}
                            className={`flex items-start gap-2 p-2 border rounded ${COLOR_BY_TIPO[item.tipo]}`}
                        >
                            <span className="text-base">{ICON_BY_TIPO[item.tipo]}</span>
                            <div className="flex-1 min-w-0">
                                {item.id > 0 && (
                                    <div className="text-sm font-medium text-slate-800">
                                        #{item.id} {item.titulo}
                                    </div>
                                )}
                                <div className="text-xs text-slate-600">{item.detalle}</div>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

const StatBox: React.FC<{ label: string; value: number; sub: string; color: 'red' | 'amber' | 'emerald' }> = ({ label, value, sub, color }) => {
    const colors = {
        red: 'bg-red-50 border-red-200 text-red-800',
        amber: 'bg-amber-50 border-amber-200 text-amber-800',
        emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    };
    return (
        <div className={`p-2 rounded-lg border ${colors[color]}`}>
            <div className="text-[10px] uppercase tracking-wide opacity-70">{label}</div>
            <div className="text-xl font-bold">{value}</div>
            {sub && <div className="text-[10px] opacity-60">{sub}</div>}
        </div>
    );
};
